/* eslint-disable react/prop-types */
import React, { lazy, useEffect } from "react";
import { Navigate, useRoutes } from "react-router-dom";
import Loadable from "../layouts/full/shared/loadable/Loadable";
import Billing from "src/views/collegeBilling/Billing";
import Users from "src/views/Users/Users";
import PlanMaster from "src/views/Plans/Plans";
import PrintOne from "src/views/counterBilling/print/PrintOne";
import SalesReport from "src/views/reports/SalesReport";
import SalesReportPrint from "src/views/counterBilling/print/SalesPrint";
import QuotationBill from "src/views/quotation";
import CollegeQuotation from "src/views/CollegeQuotation";
import DeliveryReport from "src/views/reports/DeliveryReport";
import NewReport from "src/views/reports/NewReport";
import DeliveryReportPrint from "src/views/counterBilling/print/DeliveryPrint";
import ProductWisePrint from "src/views/counterBilling/print/ProductWisePrint";
import { useDispatch, useSelector } from "react-redux";
import { CommonPrivFilter } from "src/redux/privileges/Privileges";
import { getPrivileges } from "src/redux/privileges/PrivilegesThunk";
import Loader from "src/components/Loader";
import Reciept from "src/views/RecieptAndPayment/Reciept";


const FullLayout = Loadable(lazy(() => import("../layouts/full/FullLayout")));

const Banner = Loadable(lazy(() => import("../views/banner/Banner")));
const Service = Loadable(lazy(() => import("../views/service/Service")));
const Testimonial = Loadable(
  lazy(() => import("../views/testimonial/Testimonial")),
);
const OrderList = Loadable(lazy(() => import("../views/Order/OrderList")));
const Blog = Loadable(lazy(() => import("../views/blog/Blog")));

const Error = Loadable(lazy(() => import("../views/authentication/Error")));
const Login = Loadable(lazy(() => import("../views/authentication/Login")));

// master imports
const CreateProduct = Loadable(
  lazy(() => import("src/views/master/CreateProduct")),
);
const Hostel = Loadable(lazy(() => import("src/views/master/Hostel")));
const BagMaster = Loadable(lazy(() => import("src/views/master/BagMaster")));
const TypeMaster = Loadable(lazy(() => import("src/views/master/TypeMaster")));
const RoomMaster = Loadable(lazy(() => import("src/views/master/RoomMaster")));
const CategoryMaster = Loadable(
  lazy(() => import("src/views/master/CategoryMaster")),
);
const CollegePrice = Loadable(
  lazy(() => import("src/views/master/CollegePrice")),
);
const Staff = Loadable(lazy(() => import("src/views/master/Staff")));
const StudentMaster = Loadable(
  lazy(() => import("src/views/master/StudentMaster")),
);
const LaundrySetup = Loadable(
  lazy(() => import("src/views/master/LaundrySetup")),
);
const CounterBill = Loadable(lazy(() => import("src/views/counterBilling")));
const CarpetCounterBill = Loadable(
  lazy(() => import("src/views/counterBilling/carpets/index")),
);
const CarWashCounterBill = Loadable(
  lazy(() => import("src/views/carwash/index")),
);
const VehicleTypeMaster = Loadable(
  lazy(() => import("src/views/carwash/master/VehicleType")),
);
const CarWashSetup = Loadable(
  lazy(() => import("src/views/carwash/master/CarWashSetup")),
);
const CarWashPlanMaster = Loadable(
  lazy(() => import("src/views/carwash/master/CarWashPlanMaster")),
);
const RedeemMaster = Loadable(
  lazy(() => import("src/views/carwash/master/RedeemMaster")),
);

// reports
const StudentOutstanding = Loadable(
  lazy(() => import("src/views/reports/StudentOutstanding")),
);

const SalesReportNew = Loadable(
  lazy(() => import("src/views/reports/SalesReportNew")),
);
const BagTab = Loadable(lazy(() => import("src/views/reports/BagTab")));
const BillSummary = Loadable(
  lazy(() => import("src/views/reports/BillSummary")),
);
const ProductWiseReport = Loadable(
  lazy(() => import("src/views/reports/ProductWiseReport")),
);
const ItemWiseSalesReport = Loadable(
  lazy(() => import("src/views/reports/ItemWiseSalesReport")),
);
const CategoryWiseSalesReport = Loadable(
  lazy(() => import("src/views/reports/CategoryWiseSalesReport")),
);
const CashCollectionReport = Loadable(
  lazy(() => import("src/views/reports/CashCollectionReport")),
);
const PaymentWiseReport = Loadable(
  lazy(() => import("src/views/reports/PaymentWiseReport")),
);
const UnpaidReport = Loadable(
  lazy(() => import("src/views/reports/UnpaidReport")),
);
const GeneralLedgerReport = Loadable(
  lazy(() => import("src/views/reports/GeneralLedgerReport")),
);
const OutstandingBalanceReport = Loadable(
  lazy(() => import("src/views/reports/OutstandingBalanceReport")),
);
const UndeliveredItemsReport = Loadable(
  lazy(() => import("src/views/reports/UndeliveredItemsReport")),
);
const PartialDeliveryReport = Loadable(
  lazy(() => import("src/views/reports/PartialDeliveryReport")),
);
const OutsourceReport = Loadable(
  lazy(() => import("src/views/reports/OutsourceReport")),
);
const DailyReport = Loadable(
  lazy(() => import("src/views/reports/DailyReport")),
);
const CarWashDailyReport = Loadable(
  lazy(() => import("src/views/carwash/reports/CarWashDailyReport")),
);

function ProtectedRoute({ isAllowed, children }) {
  return isAllowed ? children : <Navigate to="/404" />;
}

function Routes() {
  const { loading, userType } = useSelector((state) => state?.privilegeSlice);

  function filterRoutes(routes) {
    const predicate = (route) =>
      !route?.ref ? true : CommonPrivFilter(route.ref)?.canRead;
    // Check if current route passes the predicate
    const matches = predicate(routes);

    // Recursively filter children if they exist
    let filteredChildren = [];
    if (routes.children?.length > 0 && Array.isArray(routes.children)) {
      filteredChildren = routes.children
        .map((child) => filterRoutes(child))
        .filter(Boolean);
    }

    // Return route if it matches or has matching children
    if (matches && filteredChildren.length > 0) {
      return {
        ...routes,
        children: filteredChildren,
      };
    }
    if (matches) {
      return routes;
    }

    // Otherwise exclude the route
    return {
      ...routes,
      element: <Navigate to="/404" />,
      children: filteredChildren.length > 0 ? filteredChildren : null,
    };
  }
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getPrivileges());
  }, [dispatch]);
  const isLaundry = localStorage.getItem("isLaundry") === "true" ? true : false;
  const isCarWash = localStorage.getItem("isCarWash") === "true" ? true : false;
  const routes = useRoutes([
    filterRoutes({
      path: "/",
      element: <FullLayout />,
      children: [
        //commented first line of path home section for service default for for pro service
        // { path: "/", element: <Navigate to="/master/product" /> },
        // { path: 'dashboard', element: <Dashboard /> },

        // {
        //   index: true,
        //   element: <Home />,
        // },
        {
          path: "/",
          element: (
            <Navigate
              to={
                isLaundry
                  ? !["ADMIN", "ACCOUNTANT", "CEO", "MANAGER"].includes(userType?.toUpperCase())
                    ? "/counterBilling"
                    : "/service"
                  : "/service"
              }
            />
          ),
        },
        { path: "banner", element: <Banner />, ref: "adminconsole-banner" },
        { path: "service", element: <Service />, ref: "adminconsole-service" },
        {
          path: "testimonial",
          element: <Testimonial />,
          ref: "adminconsole-testimonial",
        },
        { path: "blog", element: <Blog />, ref: "" },
        {
          path: "orders",
          element: <OrderList />,
          ref: "adminconsole-laundry-orders",
        },
        {
          path: "users",
          element: <Users />,
          ref: "adminconsole-laundry-users",
        },
        {
          path: "plans",
          element: <PlanMaster />,
          ref: "adminconsole-laundry-plans",
        },

        {
          path: "college_bill",
          element: <Billing />,
          ref: "adminconsole-laundry-college-bill",
        },
        {
          path: "college_quotation",
          element: <CollegeQuotation />,
          ref: "adminconsole-laundry-college-quotation",
        },

        // masters
        { path: "master/product", element: <CreateProduct />, ref: "" },
        {
          path: "master/hostel",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <Hostel />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-hostel",
        },
        {
          path: "master/bag",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <BagMaster />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-bag",
        },
        {
          path: "master/type_master",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <TypeMaster />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-type-master",
        },
        {
          path: "master/room",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <RoomMaster />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-room",
        },
        {
          path: "master/category",
          element: <CategoryMaster />,
          ref: "adminconsole-category",
        },
        {
          path: "master/college",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <CollegePrice />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-college-price",
        },
        { path: "master/staff", element: <Staff />, ref: "adminconsole-staff" },
        {
          path: "master/student",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <StudentMaster />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-student",
        },
        {
          path: "master/setup",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <LaundrySetup />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-setup",
        },

        // reports (protected routes)
        {
          path: "report/student-outstanding",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <StudentOutstanding />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-student-outstanding",
        },
        {
          path: "report/sales-report",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <SalesReport />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-bill-report",
        },
        {
          path: "report/sales-report-new",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <SalesReportNew />
            </ProtectedRoute>
          ),
          // ref: "adminconsole-laundry-bill-report",
        },
        {
          path: "report/delivery-report",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <DeliveryReport />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-delivery-report",
        },
        {
          path: "report/new-report",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <NewReport />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-wise-report",
        },
        {
          path: "report/bag-report",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <BagTab />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-bag-report",
        },
        {
          path: "report/bill-summary",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <BillSummary />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-bill-summary",
        },
        {
          path: "report/product-wise-report",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <ProductWiseReport />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-product-report",
        },
        {
          path: "report/item-wise-report",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <ItemWiseSalesReport />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-itemwise-report",
        },
        {
          path: "report/category-wise-report",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <CategoryWiseSalesReport />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-category-report",
        },
        // {
        //   path: "report/cash-collection",
        //   element: (
        //     <ProtectedRoute isAllowed={isLaundry}>
        //       <CashCollectionReport />
        //     </ProtectedRoute>
        //   ),
        //    ref: "adminconsole-laundry-cash-collectionreport",
        // },
        {
          path: "report/payment-wise-report",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <PaymentWiseReport />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-payment-wise-report",
        },
        {
          path: "report/unpaid-report",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <UnpaidReport />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-payment-wise-report",
        },
        {
          path: "report/general-ledger",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <GeneralLedgerReport />
            </ProtectedRoute>
          ),
          // ref: "adminconsole-laundry-general-ledger",
        },
        {
          path: "report/outstanding-balance-report",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <OutstandingBalanceReport />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-Outstandingbalance-report",
        },
        {
          path: "report/undelivered-items",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <UndeliveredItemsReport />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-undelivered-report",
        },
        {
          path: "report/partial-delivery-report",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <PartialDeliveryReport />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-PartialDelivery-report",
        },
        {
          path: "report/outsource-report",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <OutsourceReport />
            </ProtectedRoute>
          ),
          ref: "adminconsole-laundry-outsource-report",
        },
        {
          path: "report/daily-report",
          element: (
            <ProtectedRoute isAllowed={isLaundry}>
              <DailyReport />
            </ProtectedRoute>
          ),
          // ref: "adminconsole-laundry-daily-report",
        },
        {
          path: "carwash/master/vehicle-type",
          element: (
            <ProtectedRoute isAllowed={isCarWash}>
              <VehicleTypeMaster />
            </ProtectedRoute>
          ),
          ref: "adminconsole-carwash-vehicle-master",
        },
        {
          path: "carwash/master/setup",
          element: (
            <ProtectedRoute isAllowed={isCarWash}>
              <CarWashSetup />
            </ProtectedRoute>
          ),
          ref: "adminconsole-carwash-setup",
        },
        {
          path: "carwash/master/plan-master",
          element: (
            <ProtectedRoute isAllowed={isCarWash}>
              <CarWashPlanMaster />
            </ProtectedRoute>
          ),
          ref: "adminconsole-carwash-plans",
        },
        {
          path: "carwash/master/redeem-master",
          element: (
            <ProtectedRoute isAllowed={isCarWash}>
              <RedeemMaster />
            </ProtectedRoute>
          ),
          ref: "adminconsole-carwash-redeem-master",
        },
        {
          path: "carwash/report/daily-report",
          element: (
            <ProtectedRoute isAllowed={isCarWash}>
              <CarWashDailyReport />
            </ProtectedRoute>
          ),
          ref: "adminconsole-carwash-daily-report",
        },
      ],
    }),
    filterRoutes({
      path: "counterBilling",
      element: <CounterBill />,
      ref: "adminconsole-laundry-counterbilling",
    }),
    {
      path: "counterBilling/carpets",
      element: (
        <ProtectedRoute isAllowed={isLaundry}>
          <CarpetCounterBill />
        </ProtectedRoute>
      ),
    },
    filterRoutes({
      path: "carwash/counterBilling",
      element: <CarWashCounterBill />,
      ref: "adminconsole-carwash-counterbilling",
    }),
    filterRoutes({
      path: "reciept",
      element: <Reciept />,
      ref: "adminconsole-laundry-counterbilling",
    }),
    filterRoutes({
      path: "quotationBilling",
      element: <QuotationBill />,
      ref: "adminconsole-laundry-quotationbilling",
    }),
    filterRoutes({ path: "print_one", element: <PrintOne />, ref: "" }),
    filterRoutes({
      path: "sales_report_print",
      element: <SalesReportPrint />,
      ref: "",
    }),
    filterRoutes({
      path: "delivery_report_print",
      element: <DeliveryReportPrint />,
      ref: "",
    }),
    filterRoutes({
      path: "product_wise_report_print",
      element: <ProductWisePrint />,
      ref: "",
    }),
    filterRoutes({ path: "404", element: <Error />, ref: "" }),
    filterRoutes({ path: "auth/login", element: <Login />, ref: "" }),
    filterRoutes({ path: "*", element: <Navigate to="/404" />, ref: "" }),
  ]);
  if (loading) {
    return <Loader start={loading} />;
  }
  return routes;
}

export default Routes;
